import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/api';
import ReCAPTCHA from 'react-google-recaptcha';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const captchaRef = useRef(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  /* ── Forgot Password state ── */
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotMsg, setForgotMsg] = useState('');
  const [forgotErr, setForgotErr] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!captchaToken) {
      setError('Please complete the CAPTCHA verification');
      return;
    }

    setLoading(true);
    try {
      const user = await login(email, password, captchaToken);
      const routes = { admin: '/admin', csr: '/csr', individual: '/individual', agency: '/agency' };
      navigate(routes[user.role] || '/');
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed');
      // Reset captcha on failure
      if (captchaRef.current) captchaRef.current.reset();
      setCaptchaToken(null);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotMsg('');
    setForgotErr('');
    if (!forgotEmail) { setForgotErr('Please enter your email address'); return; }
    setForgotLoading(true);
    try {
      await authAPI.forgotPassword({ email: forgotEmail });
      setForgotMsg('If this email is registered, a password reset link has been sent.');
    } catch (err) {
      setForgotMsg('If this email is registered, a password reset link has been sent.');
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="auth-container" style={{
      backgroundImage: 'url(/images/hero-forest.jpg)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    }}>
      <div style={{
        position: 'absolute', inset: 0,
        background: 'linear-gradient(135deg, rgba(13,79,28,0.88) 0%, rgba(26,116,49,0.82) 50%, rgba(0,191,165,0.7) 100%)',
      }} />
      <div className="auth-card" style={{ position: 'relative', zIndex: 2 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14, marginBottom: 16 }}>
          <img src={`${process.env.PUBLIC_URL || ''}/haritanjali_logo.png`} alt="Haritanjali"
            style={{ width: 94, height: 94, objectFit: 'contain' }} />
          <div style={{ textAlign: 'left' }}>
            <h1 style={{ fontSize: 32, marginBottom: 0, color: '#2d5016', fontWeight: 800, lineHeight: 1.1 }}>हरितांजलि</h1>
            <p style={{ fontSize: 13, color: '#2d5016', letterSpacing: 4, textTransform: 'uppercase', margin: '2px 0 0 0', fontWeight: 600 }}>HARITANJALI</p>
            <p style={{ fontSize: 13, color: '#E8751A', fontStyle: 'italic', margin: '4px 0 0 0', fontWeight: 500 }}>जहाँ पेड़, वहाँ जीवन</p>
          </div>
        </div>
        <p className="subtitle">{showForgot ? 'Reset your password' : 'Sign in to your account'}</p>

        {!showForgot ? (
          <>
            {error && <div className="error">{error}</div>}

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Email Address</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email" required />
              </div>
              <div className="form-group">
                <label>Password</label>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password" required />
              </div>

              {/* Google reCAPTCHA */}
              <div style={{ display: 'flex', justifyContent: 'center', margin: '12px 0' }}>
                <ReCAPTCHA
                  ref={captchaRef}
                  sitekey={RECAPTCHA_SITE_KEY}
                  onChange={(token) => setCaptchaToken(token)}
                  onExpired={() => setCaptchaToken(null)}
                />
              </div>

              <button type="submit" className="btn btn-primary btn-block" disabled={loading || !captchaToken}
                style={{ padding: '12px 20px', fontSize: 15, borderRadius: 30, marginTop: 8 }}>
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>

            <div style={{ textAlign: 'right', marginTop: 10 }}>
              <button
                onClick={() => { setShowForgot(true); setError(''); }}
                style={{ background: 'none', border: 'none', color: '#2e7d32', fontSize: 13, fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}
              >
                Forgot Password?
              </button>
            </div>

            <div className="auth-footer" style={{ marginTop: 16 }}>
              Don't have an account? <Link to="/register" style={{ fontWeight: 600 }}>Register here</Link>
            </div>
            <div style={{ textAlign: 'center', marginTop: 12 }}>
              <Link to="/" style={{ fontSize: 13, color: '#888' }}>Back to Homepage</Link>
            </div>
          </>
        ) : (
          <>
            {forgotErr && <div className="error">{forgotErr}</div>}
            {forgotMsg && <div className="success">{forgotMsg}</div>}

            {!forgotMsg && (
              <form onSubmit={handleForgotPassword}>
                <div className="form-group">
                  <label>Email Address</label>
                  <input type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)}
                    placeholder="Enter your registered email" required />
                </div>
                <p style={{ fontSize: 12, color: '#888', marginTop: -4, marginBottom: 12 }}>
                  We'll send a temporary password to your registered email address.
                </p>
                <button type="submit" className="btn btn-primary btn-block" disabled={forgotLoading}
                  style={{ padding: '12px 20px', fontSize: 15, borderRadius: 30 }}>
                  {forgotLoading ? 'Sending...' : 'Reset Password'}
                </button>
              </form>
            )}

            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <button
                onClick={() => { setShowForgot(false); setForgotMsg(''); setForgotErr(''); }}
                style={{ background: 'none', border: 'none', color: '#2e7d32', fontSize: 13, fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}
              >
                &larr; Back to Sign In
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
